import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { NationWarsLeaderboard } from "@/components/nationwars/NationWarsLeaderboard";
import { NationalCrewsWidget } from "@/components/ambassadors/NationalCrewsWidget";
import { GuerrillaInviteButton } from "@/components/viral/GuerrillaInviteButton";
import { NationGate } from "@/components/onboarding/NationGate";
import { ActivationBanner } from "@/components/onboarding/ActivationBanner";
import { resolveParticipateRoute } from "@/lib/predictions/participateRoute";
import { trackEvent } from "@/lib/audit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Target,
  Zap,
  Swords,
  ArrowRight,
  Shield,
  Crown,
  Share2,
  MessageCircle,
} from "lucide-react";
import {
  buildNationWarsMessage,
  buildNationWarsUrl,
  buildOgTitle,
  getNationFlag,
  getNationName,
  getNationOgImageUrl,
  getNationWarsOgDescription,
} from "@/lib/share/nationWarsShare";
import { shareNation } from "@/lib/whatsappViral/whatsappEngine";
import { useLanguage } from "@/hooks/useLanguage";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";

const ALL_SHARE_NATIONS: ReadonlyArray<{ code: string; name: string; flag: string }> = [
  // P1-NATION-UI-01 — ISO-3 to match `profiles.selection_team`, `worldcup_teams.code`,
  // and `nation_scores.nation_code`. No ISO-2/ISO-3 conversions anywhere in Nation Wars.
  { code: "BRA", name: "Brasil", flag: "🇧🇷" },
  { code: "ARG", name: "Argentina", flag: "🇦🇷" },
  { code: "MEX", name: "México", flag: "🇲🇽" },
  { code: "COL", name: "Colombia", flag: "🇨🇴" },
  { code: "USA", name: "USA", flag: "🇺🇸" },
  { code: "ECU", name: "Ecuador", flag: "🇪🇨" },
  { code: "ESP", name: "España", flag: "🇪🇸" },
  { code: "POR", name: "Portugal", flag: "🇵🇹" },
];

// PREVIEW DATA CERTIFICATION — During PREVIEW_MODE only the two Final
// contenders (ESP / ARG) are exposed. All other nations are deferred to
// post-launch and must not leak into share pickers, rivalry cards, or
// default selections.
const SHARE_NATIONS: ReadonlyArray<{ code: string; name: string; flag: string }> = PREVIEW_MODE
  ? [
      { code: OFFICIAL_MATCH.homeCode, name: OFFICIAL_MATCH.homeName, flag: OFFICIAL_MATCH.homeFlag },
      { code: OFFICIAL_MATCH.awayCode, name: OFFICIAL_MATCH.awayName, flag: OFFICIAL_MATCH.awayFlag },
    ]
  : ALL_SHARE_NATIONS;

const searchSchema = z.object({
  nation: z.string().trim().min(2).max(3).toUpperCase().optional().catch(undefined),
});

export const Route = createFileRoute("/nation-wars")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ nation: search.nation ?? null }),
  loader: ({ deps }) => ({ nation: deps.nation }),
  head: ({ loaderData }) => {
    const nation = loaderData?.nation ?? null;
    const title = buildOgTitle(nation);
    const description = getNationWarsOgDescription();
    const url = buildNationWarsUrl(nation);
    const image = getNationOgImageUrl(nation);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: NationWarsPage,
});

function NationWarsPage() {
  const { lang, t } = useLanguage();
  const [isEmpty, setIsEmpty] = useState<boolean | null>(null);
  const { nation: nationFromUrl } = Route.useSearch();
  const { nation: userNation } = useSelectedNation();
  // P1-NATION-UI-01 — Initial selection respects (in order):
  //   1. ?nation=XXX in the URL (explicit override, e.g. shared link)
  //   2. profiles.selection_team (user's real nation, ISO-3)
  //   3. "BRA" as last-resort fallback for unauthenticated visitors.
  const [selectedNation, setSelectedNation] = useState<string>(
    nationFromUrl ?? userNation ?? (PREVIEW_MODE ? OFFICIAL_MATCH.homeCode : "BRA"),
  );
  const navigate = useNavigate();
  const [participateLoading, setParticipateLoading] = useState(false);

  const onParticipate = async () => {
    if (participateLoading) return;
    setParticipateLoading(true);
    try {
      const target = await resolveParticipateRoute();
      void trackEvent("participate_cta_resolved", {
        source: "nation_wars_cta",
        kind: target.kind,
        reason: target.reason,
        to: target.to,
      });
      if (target.kind === "match_viewer") {
        await navigate({ to: "/", search: { open: "predictions" } as never });
      } else {
        await navigate({ to: "/quinielas" });
      }
    } finally {
      setParticipateLoading(false);
    }
  };

  useEffect(() => {
    if (nationFromUrl) setSelectedNation(nationFromUrl);
  }, [nationFromUrl]);

  // Sync once the user's real nation loads (only if URL didn't pin one and
  // the user hasn't manually picked a different flag yet).
  const [userTouched, setUserTouched] = useState(false);
  useEffect(() => {
    if (nationFromUrl) return;
    if (userTouched) return;
    if (userNation) setSelectedNation(userNation);
  }, [nationFromUrl, userNation, userTouched]);

  useEffect(() => {
    supabase
      .from("nation_scores")
      .select("*", { count: "exact", head: true })
      .then(({ count }) => setIsEmpty((count ?? 0) === 0));
  }, []);

  const previewMessage = useMemo(
    () => buildNationWarsMessage(selectedNation, lang),
    [selectedNation, lang],
  );
  const previewUrl = useMemo(
    () => buildNationWarsUrl(selectedNation),
    [selectedNation],
  );

  const onNativeShare = async () => {
    const res = await shareNation(
      { nationCode: selectedNation },
      { source: "nation_wars_page", lang },
    );
    if (!res.ok) toast.error(t("cm_copy_failed"));
  };

  const onWhatsAppShare = () => {
    void shareNation(
      { nationCode: selectedNation },
      { source: "nation_wars_page", lang, preferNative: false },
    );
  };

  return (
    <main className="container mx-auto p-4 md:p-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold">Nation Wars™</h1>
        <p className="text-sm text-muted-foreground">
          Cada predicción correcta suma puntos a tu nación. Tiempo real.
        </p>
      </header>
      {/* Preview the leaderboard BEFORE the gate so visitors see value first. */}
      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Ranking mundial · ahora mismo
        </h3>
        <NationWarsLeaderboard limit={10} />
      </section>
      <NationGate surface="nation_wars" mode="invite" />
      <ActivationBanner surface="nation_wars" />

      {isEmpty === true && (
        <Card className="glass overflow-hidden border-border/60">
          <CardContent className="p-6 md:p-8 text-center space-y-3">
            <div className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-amber-300 to-yellow-500 grid place-items-center shadow-lg">
              <Trophy className="h-7 w-7 text-background" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold">
              La batalla mundial está por comenzar.
            </h2>
            <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-relaxed">
              Participa en predicciones en vivo y ayuda a que tu nación domine el ranking global.
              Las primeras predicciones correctas definirán el liderazgo mundial.
            </p>
          </CardContent>
        </Card>
      )}

      {/* 1. Share challenge — viral entry point */}
      <Card className="border-border/60 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <CardContent className="p-5 md:p-6 space-y-4">
          <div className="space-y-1">
            <h3 className="text-base md:text-lg font-bold flex items-center gap-2">
              <Share2 className="h-5 w-5 text-primary" />
              Reta a tu nación
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground">
              Cada share es un reto nacional. Elige tu bandera y reclutá a tu gente.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {SHARE_NATIONS.map((n) => {
              const active = selectedNation === n.code;
              return (
                <button
                  key={n.code}
                  type="button"
                  onClick={() => {
                    setUserTouched(true);
                    setSelectedNation(n.code);
                  }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary shadow"
                      : "bg-card/60 border-border/60 hover:border-primary/40"
                  }`}
                  aria-pressed={active}
                >
                  <span className="mr-1">{n.flag}</span>
                  {n.name}
                </button>
              );
            })}
          </div>

          <div className="rounded-lg border border-border/40 bg-background/60 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-2xl leading-none">{getNationFlag(selectedNation)}</span>
              <p className="text-sm leading-snug">{previewMessage}</p>
            </div>
            <code className="block truncate text-[11px] text-muted-foreground">{previewUrl}</code>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button onClick={onNativeShare} size="lg" className="gap-2">
              <Share2 className="h-4 w-4" />
              {t("nw_share_cta", { name: getNationName(selectedNation, lang) })}
            </Button>
            <Button
              onClick={onWhatsAppShare}
              size="lg"
              variant="secondary"
              className="gap-2 bg-[#25D366] text-white hover:bg-[#1ebe5d]"
            >
              <MessageCircle className="h-4 w-4" />
              Enviar por WhatsApp
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 2. Participate CTA — resolves to live match or predictions center */}
      <Card className="border-border/60 bg-gradient-to-r from-primary/10 to-accent/10">
        <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-base font-semibold">Únete a la batalla mundial</h3>
            <p className="text-sm text-muted-foreground">
              Cada predicción cuenta. Representa a tu nación y escala el ranking.
            </p>
          </div>
          <Button
            onClick={onParticipate}
            disabled={participateLoading}
            size="lg"
            className="gap-2"
          >
            {participateLoading ? "Abriendo…" : "Participar en predicciones"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      {/* 3. Rivalries */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Swords className="h-5 w-5 text-primary" /> Rivalidades destacadas
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PREVIEW_MODE ? (
            <RivalryCard
              left={{ flag: OFFICIAL_MATCH.homeFlag, name: OFFICIAL_MATCH.homeName }}
              right={{ flag: OFFICIAL_MATCH.awayFlag, name: OFFICIAL_MATCH.awayName }}
            />
          ) : (
            <>
              <RivalryCard left={{ flag: "🇧🇷", name: "Brasil" }} right={{ flag: "🇦🇷", name: "Argentina" }} />
              <RivalryCard left={{ flag: "🇺🇸", name: "USA" }} right={{ flag: "🇲🇽", name: "México" }} />
              <RivalryCard left={{ flag: "🇨🇴", name: "Colombia" }} right={{ flag: "🇪🇨", name: "Ecuador" }} />
            </>
          )}
        </div>
      </section>

      {/* 4. National Crews */}
      <NationalCrewsWidget />

      {/* WORLD-CUP-VIRAL-ACTIVATION-P0 — viral invite CTA */}
      <GuerrillaInviteButton />

      {/* 5. World Ranking — full leaderboard at the bottom */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" /> Ranking Mundial
        </h3>
        <NationWarsLeaderboard limit={32} />
      </section>

      {/* 6. How it works — explainer */}
      <section className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" /> ¿Cómo funciona?
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <HowItWorksCard icon={<Target className="h-5 w-5" />} text="Acierta predicciones." />
          <HowItWorksCard icon={<Zap className="h-5 w-5" />} text="Gana XP." />
          <HowItWorksCard icon={<Shield className="h-5 w-5" />} text="Tu XP suma a tu nación." />
          <HowItWorksCard icon={<Swords className="h-5 w-5" />} text="Compite contra otros países." />
          <HowItWorksCard icon={<Crown className="h-5 w-5" />} text="Escala en el ranking mundial." />
        </div>
      </section>
    </main>
  );
}

function HowItWorksCard({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Card className="border-border/40 bg-card/50">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/10 grid place-items-center text-primary">
          {icon}
        </div>
        <span className="text-sm font-medium">{text}</span>
      </CardContent>
    </Card>
  );
}

function RivalryCard({ left, right }: { left: { flag: string; name: string }; right: { flag: string; name: string } }) {
  return (
    <Card className="border-border/40 bg-card/50 overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-2xl leading-none">{left.flag}</span>
            <span className="text-sm font-semibold">{left.name}</span>
          </div>
          <span className="text-xs font-bold text-primary uppercase tracking-wider">vs</span>
          <div className="flex items-center gap-2 flex-1 justify-end">
            <span className="text-sm font-semibold">{right.name}</span>
            <span className="text-2xl leading-none">{right.flag}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
